import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  disconnectProviderConnection,
  getProviderConnections,
  saveClaudeProviderToken,
  startProviderConnection,
  type ProviderConnectionAttempt,
  type ProviderConnectionProvider,
  type ProviderConnectionStatus,
} from '@/lib/api';

const PROVIDER_LABELS: Record<ProviderConnectionProvider, string> = {
  claude: 'Claude',
  codex: 'Codex',
};

const CLAUDE_SETUP_COMMAND = 'claude setup-token';

function isPending(
  attempt: ProviderConnectionAttempt | null | undefined
): attempt is ProviderConnectionAttempt {
  return attempt?.status === 'pending';
}

function PendingAttempt({ attempt }: { attempt: ProviderConnectionAttempt }): React.ReactElement {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
      <div className="flex flex-col gap-2">
        {attempt.authUrl && (
          <a
            href={attempt.authUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-4 hover:underline"
          >
            {attempt.authUrl}
          </a>
        )}
        {attempt.userCode && (
          <Input
            readOnly
            value={attempt.userCode}
            aria-label={`${PROVIDER_LABELS[attempt.provider]} authorization code`}
            className="max-w-xs font-mono"
          />
        )}
        <div className="text-xs text-muted-foreground">Expires {attempt.expiresAt}</div>
      </div>
    </div>
  );
}

interface ClaudeTokenFormProps {
  onSubmit: (token: string) => void;
  isPending: boolean;
  error: string | null;
}

function ClaudeTokenForm({ onSubmit, isPending, error }: ClaudeTokenFormProps): React.ReactElement {
  const [token, setToken] = useState('');
  const trimmed = token.trim();
  const canSubmit = trimmed.length > 0 && !isPending;

  return (
    <form
      className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm"
      onSubmit={event => {
        event.preventDefault();
        if (canSubmit) {
          onSubmit(trimmed);
        }
      }}
    >
      <p className="text-sm">
        Run <code className="rounded bg-muted px-1 py-0.5 font-mono">{CLAUDE_SETUP_COMMAND}</code>{' '}
        in your terminal, authorize in your browser, then paste the{' '}
        <span className="font-mono">sk-ant-oat01-…</span> token it prints below.
      </p>
      <Textarea
        value={token}
        onChange={event => {
          setToken(event.target.value);
        }}
        placeholder="sk-ant-oat01-..."
        aria-label="Claude OAuth setup-token"
        rows={3}
        className="font-mono text-xs"
        disabled={isPending}
      />
      {error && <div className="text-sm text-destructive">{error}</div>}
      <div>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

interface ConnectionRowProps {
  connection: ProviderConnectionStatus;
  startedAttempt?: ProviderConnectionAttempt;
  supported: boolean;
  onConnect: (provider: ProviderConnectionProvider) => void;
  onDisconnect: (provider: ProviderConnectionProvider) => void;
  onSaveClaudeToken: (token: string) => void;
  connectPending: boolean;
  disconnectPending: boolean;
  claudeSavePending: boolean;
  claudeSaveError: string | null;
}

function ConnectionRow(props: ConnectionRowProps): React.ReactElement {
  const {
    connection,
    startedAttempt,
    supported,
    connectPending,
    disconnectPending,
    claudeSavePending,
    claudeSaveError,
  } = props;
  const attempt = connection.activeAttempt ?? startedAttempt ?? null;
  const [showClaudeForm, setShowClaudeForm] = useState(false);
  const isClaude = connection.provider === 'claude';

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{PROVIDER_LABELS[connection.provider]}</div>
          {connection.configured && (
            <div className="text-xs text-muted-foreground">
              {connection.preview} · updated {connection.updatedAt ?? 'unknown'}
            </div>
          )}
        </div>
        {connection.configured ? (
          <Badge variant="default">Connected</Badge>
        ) : isPending(attempt) ? (
          <Badge variant="outline">Pending</Badge>
        ) : (
          <Badge variant="outline">Not connected</Badge>
        )}
      </div>

      {/* Codex uses the broker flow; Claude uses the manual paste flow. */}
      {!isClaude && isPending(attempt) && <PendingAttempt attempt={attempt} />}
      {!isClaude && attempt?.status === 'failed' && attempt.safeError && (
        <div className="text-sm text-destructive">{attempt.safeError}</div>
      )}

      {isClaude && !connection.configured && showClaudeForm && (
        <ClaudeTokenForm
          onSubmit={token => {
            props.onSaveClaudeToken(token);
          }}
          isPending={claudeSavePending}
          error={claudeSaveError}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {!connection.configured && isClaude && !showClaudeForm && (
          <Button
            type="button"
            onClick={() => {
              setShowClaudeForm(true);
            }}
            disabled={!supported}
          >
            Connect
          </Button>
        )}
        {!connection.configured && isClaude && showClaudeForm && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setShowClaudeForm(false);
            }}
            disabled={claudeSavePending}
          >
            Cancel
          </Button>
        )}
        {!connection.configured && !isClaude && (
          <Button
            type="button"
            onClick={() => {
              props.onConnect(connection.provider);
            }}
            disabled={!supported || connectPending || isPending(attempt)}
          >
            Connect
          </Button>
        )}
        {connection.configured && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              props.onDisconnect(connection.provider);
            }}
            disabled={!supported || disconnectPending}
          >
            Disconnect
          </Button>
        )}
      </div>
    </div>
  );
}

export function ProviderConnectionsCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const [startedAttempts, setStartedAttempts] = useState<
    Partial<Record<ProviderConnectionProvider, ProviderConnectionAttempt>>
  >({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [claudeSaveError, setClaudeSaveError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['providerConnections'],
    queryFn: getProviderConnections,
    refetchInterval: query =>
      query.state.data?.connections?.some(connection => isPending(connection.activeAttempt))
        ? 1500
        : false,
  });

  const connectMutation = useMutation({
    mutationFn: startProviderConnection,
    onSuccess: async result => {
      setMutationError(null);
      setStartedAttempts(prev => ({
        ...prev,
        [result.attempt.provider]: result.attempt,
      }));
      await queryClient.invalidateQueries({ queryKey: ['providerConnections'] });
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectProviderConnection,
    onSuccess: async (_result, provider) => {
      setMutationError(null);
      setStartedAttempts(prev => {
        return Object.fromEntries(
          Object.entries(prev).filter(([entryProvider]) => entryProvider !== provider)
        ) as Partial<Record<ProviderConnectionProvider, ProviderConnectionAttempt>>;
      });
      await queryClient.invalidateQueries({ queryKey: ['providerConnections'] });
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  const claudeTokenMutation = useMutation({
    mutationFn: saveClaudeProviderToken,
    onMutate: () => {
      setClaudeSaveError(null);
    },
    onSuccess: async () => {
      setMutationError(null);
      setClaudeSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['providerConnections'] });
    },
    onError: (err: Error) => {
      setClaudeSaveError(err.message);
    },
  });

  const supported = data?.supported ?? true;
  const connections = useMemo<ProviderConnectionStatus[]>(() => data?.connections ?? [], [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Provider Connections</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {error instanceof Error && (
          <div className="text-sm text-destructive">
            Failed to load provider connections: {error.message}
          </div>
        )}
        {!supported && (
          <div className="mb-3 text-sm text-muted-foreground">
            Provider connections are not supported in this environment yet.
          </div>
        )}
        {mutationError && <div className="mb-3 text-sm text-destructive">{mutationError}</div>}
        <div className="space-y-3">
          {connections.map(connection => (
            <ConnectionRow
              key={connection.provider}
              connection={connection}
              startedAttempt={startedAttempts[connection.provider]}
              supported={supported}
              onConnect={provider => {
                connectMutation.mutate(provider);
              }}
              onDisconnect={provider => {
                disconnectMutation.mutate(provider);
              }}
              onSaveClaudeToken={token => {
                claudeTokenMutation.mutate(token);
              }}
              connectPending={connectMutation.isPending}
              disconnectPending={disconnectMutation.isPending}
              claudeSavePending={claudeTokenMutation.isPending}
              claudeSaveError={claudeSaveError}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
